// BusDetails.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaBus,
  FaPhone,
  FaGlobe,
  FaMobileAlt,
  FaShareAlt,
  FaMapMarkerAlt,
  FaClock,
  FaExternalLinkAlt,
  FaBars,
  FaTimes,
  FaArrowRight,
  FaHome
} from 'react-icons/fa';
import { MdDirectionsBus } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { setLanguage, selectLanguage } from '../redux/Slices/busSlice';
import logo from '../../components/assets/logos/logo2.PNG';
import BaseUrl from '../Url/BaseUrl';

const BusDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector(selectLanguage);
  const { bus, rawBusData, formData, fullApiResponse } = location.state || {};

  const [expandedStops, setExpandedStops] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('timetable');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);

  const [forwardStops, setForwardStops] = useState([]);
  const [busInfo, setBusInfo] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState({ booking: [], operator: [] });
  const [websites, setWebsites] = useState([]);
  const [apps, setApps] = useState([]);
  const [isLoadingStops, setIsLoadingStops] = useState(false);
  const [busStandData, setBusStandData] = useState(null);

  const COLORS = {
    PRIMARY_YELLOW: '#FFF9C4',
    SECONDARY_YELLOW: '#ffffff',
    ACCENT_YELLOW: '#FFD54F',
    DARK_YELLOW: '#FFB300',
    DARK_BROWN: '#5D4037',
    DARK_GRAY: '#333',
    MEDIUM_GRAY: '#666',
    LIGHT_GRAY: '#F5F5F5',
    WHITE: '#FFFFFF',
    GREEN: '#4CAF50',
    BLUE: '#2196F3',
    RED: '#f44336',
    GREEN_LIGHT: '#E8F5E9',
    BLUE_LIGHT: '#E3F2FD',
    GRAY_LIGHT: '#F5F5F5',
    // NEW: colors matching the design images
    ORANGE_HEADER: '#C94A00',      // dark orange header band
    ORANGE_BORDER: '#D35400',      // orange border for arrival pill
    ORANGE_TEXT: '#D35400',        // orange time text
    ORANGE_BAR: '#D35400',         // left vertical bar
    NOTE_BG: '#F7F7F7',            // note box background
    STOP_CARD_BG: '#FAFAFA'        // subtle stop card bg
  };

  // ============================================================
  // FIX #2: Safer redirect guard.
  // iOS Safari bfcache restore can wipe location.state, so we only
  // redirect when we truly have no data at all.
  // ============================================================
  useEffect(() => {
    if (!location.state && !bus && !rawBusData) {
      navigate('/bus-finder', { replace: true });
    }
  }, [location.state, bus, rawBusData, navigate]);

  // ============================================================
  // FIX #4: Handle iOS bfcache restore.
  // When returning from Google Maps app on iOS, Safari may restore
  // the page from the back-forward cache. This listener ensures the
  // page re-renders correctly and scroll is reset.
  // ============================================================
  useEffect(() => {
    const handlePageShow = (event) => {
      // event.persisted === true means the page was restored from bfcache
      if (event.persisted) {
        // Force a re-render by scrolling to top (React will re-render naturally)
        window.scrollTo(0, 0);
        // Ensure visibility — some iOS versions leave the page hidden
        document.body.style.visibility = 'visible';
        document.body.style.opacity = '1';
      }
    };

    // Also handle visibilitychange as a fallback for iOS
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        document.body.style.visibility = 'visible';
        document.body.style.opacity = '1';
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const parsePhoneNumbers = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map(num => num.toString().trim()).filter(num => num.length > 0);
    }
    if (typeof value === 'string') {
      return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
    }
    return [];
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    try {
      const parts = timeStr.split(':');
      if (parts.length >= 2) {
        const hours = parseInt(parts[0]);
        const minutes = parts[1];
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes} ${period}`;
      }
      return timeStr;
    } catch {
      return timeStr;
    }
  };

  const resolveStopName = (busStand, fallbackCode = '') => {
    if (!busStand) return fallbackCode;
    let name = busStand.busStandName || fallbackCode;
    if (currentLanguage === 'hi' && busStand.hindi) {
      name = busStand.hindi;
    }
    let cityName = '';
    if (busStand.cityVillage) {
      if (currentLanguage === 'hi' && busStand.cityVillage.hindi) {
        cityName = busStand.cityVillage.hindi;
      } else if (busStand.cityVillage.cityVillageName) {
        cityName = busStand.cityVillage.cityVillageName;
      }
    }
    return cityName ? `${name}, ${cityName}` : name;
  };

  const buildForwardList = (data) => {
    const { arrival, busStands } = data;

    const arrivalTimings = Array.isArray(arrival) ? arrival : [];

    const findBusStand = (timing) => {
      if (timing.busStand) return timing.busStand;
      if (busStands && Array.isArray(busStands)) {
        return busStands.find(bs => bs.busStandCode === timing.busStandCode);
      }
      return null;
    };

    const sortedForward = [...arrivalTimings].sort((a, b) => {
      const sortA = a.sortId ?? 0;
      const sortB = b.sortId ?? 0;
      return sortA - sortB;
    });

    const forwardList = sortedForward
      .filter(timing => timing.arrivalTime)
      .map((timing, idx) => {
        const bs = findBusStand(timing);
        const gps = bs?.latitude && bs?.longitude
          ? { lat: bs.latitude, lng: bs.longitude }
          : null;
        return {
          id: `fwd-${timing.id || idx}`,
          rawId: timing.id,
          sortId: timing.sortId,
          stopName: resolveStopName(bs, timing.busStandCode),
          busStandCode: timing.busStandCode,
          time: formatTime(timing.arrivalTime),
          rawTime: timing.arrivalTime,
          gps,
          description: bs?.description || ''
        };
      });

    return forwardList;
  };

  const fetchBusStands = async (busNo) => {
    if (!busNo) return;
    setIsLoadingStops(true);
    try {
      const response = await fetch(`${BaseUrl.baseurl}Buses/GetBusByBusNoOrRegNo/${busNo}`);
      if (!response.ok) {
        throw new Error('Failed to fetch bus stand data');
      }
      const data = await response.json();
      setBusStandData(data);

      if (data.arrival && data.arrival.length > 0) {
        const forwardList = buildForwardList(data);
        setForwardStops(forwardList);
      } else {
        setForwardStops([]);
      }
    } catch (error) {
      console.error('Error fetching bus stands:', error);
      setForwardStops([]);
    } finally {
      setIsLoadingStops(false);
    }
  };

  useEffect(() => {
    if (!fullApiResponse || !rawBusData || !bus) {
      if (rawBusData?.busNo) {
        fetchBusStands(rawBusData.busNo);
      } else if (bus?.busNo) {
        fetchBusStands(bus.busNo);
      }
      return;
    }

    const busNo = rawBusData.busNo;

    const arrivalFiltered = (fullApiResponse.arrival || []).filter(t => t.busNo === busNo);

    const filteredData = {
      ...fullApiResponse,
      arrival: arrivalFiltered
    };

    const forwardList = buildForwardList(filteredData);
    setForwardStops(forwardList);

    setBusInfo({
      name: bus.name || rawBusData.busName || 'Bus',
      regNo: bus.busNumber || rawBusData.busRegistrationNumber || '',
      operator: bus.operator || rawBusData.operator?.operatorName || '',
      departure: bus.departure,
      arrival: bus.arrival,
      duration: bus.duration,
      from: formData?.fromCityName || '',
      to: formData?.toCityName || '',
      type: bus.type,
      price: bus.price
    });

    let bookingNumbers = [];
    let operatorNumbers = [];

    if (bus?.contact && bus.contact !== 'N/A') {
      bookingNumbers = parsePhoneNumbers(bus.contact);
    }

    if (rawBusData?.busContactNumber) {
      const apiNumbers = parsePhoneNumbers(rawBusData.busContactNumber);
      bookingNumbers = [...new Set([...bookingNumbers, ...apiNumbers])];
    }

    if (rawBusData?.operator?.busOperatorContactNumber) {
      operatorNumbers = parsePhoneNumbers(rawBusData.operator.busOperatorContactNumber);
    }

    const bookingContacts = bookingNumbers.map((num, idx) => ({
      id: `booking-${idx}`,
      number: num,
      type: t('busContact') || 'Bus Contact'
    }));

    const operatorContacts = operatorNumbers.map((num, idx) => ({
      id: `operator-${idx}`,
      number: num,
      type: t('operatorContact') || 'Operator Contact'
    }));

    setPhoneNumbers({ booking: bookingContacts, operator: operatorContacts });

    const dynamicWebsites = [];
    const dynamicApps = [];

    if (rawBusData?.operator?.websiteContactLink) {
      dynamicWebsites.push({
        id: 'website-1',
        name: currentLanguage === 'hi' ? 'आधिकारिक वेबसाइट' : 'Official Website',
        url: rawBusData.operator.websiteContactLink,
        type: 'website'
      });
    }

    if (rawBusData?.operator?.appLink) {
      dynamicApps.push({
        id: 'android-1',
        name: currentLanguage === 'hi' ? 'एंड्रॉइड एप्प' : 'Android App',
        url: rawBusData.operator.appLink,
        platform: 'android',
        type: 'app'
      });
    }

    if (rawBusData?.operator?.iOSAppLink) {
      dynamicApps.push({
        id: 'ios-1',
        name: currentLanguage === 'hi' ? 'आई.ओ.एस. एप्प' : 'iOS App',
        url: rawBusData.operator.iOSAppLink,
        platform: 'ios',
        type: 'app'
      });
    }

    setWebsites(dynamicWebsites);
    setApps(dynamicApps);
  }, [fullApiResponse, rawBusData, bus, formData, currentLanguage, t]);

  useEffect(() => {
    if (rawBusData?.busNo && !fullApiResponse) {
      fetchBusStands(rawBusData.busNo);
    }
  }, [rawBusData, fullApiResponse]);

  useEffect(() => {
    if (bus?.busNo && !fullApiResponse && !rawBusData?.busNo) {
      fetchBusStands(bus.busNo);
    }
  }, [bus, fullApiResponse]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    dispatch(setLanguage(lng));
    localStorage.setItem('preferred-language', lng);
    document.documentElement.dir = (lng === 'ar' || lng === 'he') ? 'rtl' : 'ltr';

    if (rawBusData?.busNo) {
      fetchBusStands(rawBusData.busNo);
    } else if (bus?.busNo) {
      fetchBusStands(bus.busNo);
    }
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('preferred-language') || 'en';
    if (savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
      dispatch(setLanguage(savedLanguage));
    }
    document.documentElement.dir = (savedLanguage === 'ar' || savedLanguage === 'he') ? 'rtl' : 'ltr';
  }, [i18n, dispatch]);

  useEffect(() => {
    const checkScreenSize = () => setIsMobile(window.innerWidth <= 768);
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  const toggleStopExpansion = (id) => {
    setExpandedStops(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // ============================================================
  // FIX #1: iOS Safari blank-page fix.
  // window.open() with universal links (Google Maps) breaks iOS
  // Safari's bfcache restoration. Using a programmatic anchor click
  // works reliably and lets iOS restore the page on return.
  // ============================================================
  const openGPSLocation = (coordinates) => {
    if (!coordinates) return;

    const url = `https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`;

    // iOS Safari handles anchor-tag navigation to universal links much better
    // than window.open(), and correctly restores the page when returning.
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const makePhoneCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const homepageUrl = 'https://rajdoot.biz';

  const shareBusDetails = async () => {
    const shareData = {
      title: 'Rajdoot Bus Services',
      text: currentLanguage === 'hi' ? 'राजदूत बस सेवाओं की वेबसाइट देखें:' : 'Check out Rajdoot Bus Services website:',
      url: homepageUrl
    };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        await fallbackCopyToClipboard();
      }
    } else {
      await fallbackCopyToClipboard();
    }
  };

  const fallbackCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(homepageUrl);
      setShowCopiedMessage(true);
      setTimeout(() => setShowCopiedMessage(false), 3000);
    } catch (error) {}
  };

  // ------------------------------------------------------------
  // STYLES
  // ------------------------------------------------------------
  const styles = {
    container: {
      backgroundColor: COLORS.WHITE,
      minHeight: '100vh',
      direction: currentLanguage === 'ar' || currentLanguage === 'he' ? 'rtl' : 'ltr',
      width: '100%',
      padding: 0,
      margin: 0,
      fontFamily: "'Noto Serif Devanagari', serif",
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      background: COLORS.PRIMARY_YELLOW,
      color: COLORS.DARK_BROWN,
      padding: isMobile ? '12px 15px' : '15px 20px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      position: 'sticky',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      width: '100%',
      boxSizing: 'border-box',
      borderBottom: `3px solid ${COLORS.DARK_YELLOW}`
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      maxWidth: '1200px',
      margin: '0 auto',
      width: '100%'
    },
    backButton: {
      background: 'transparent',
      border: `2px solid ${COLORS.DARK_BROWN}`,
      color: COLORS.DARK_BROWN,
      fontSize: isMobile ? '15px' : '17px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      borderRadius: '25px',
      transition: 'all 0.3s ease',
      fontWeight: '600',
    },
    logoContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    logoImage: {
      height: isMobile ? '35px' : '45px',
      width: 'auto',
      borderRadius: '6px'
    },
    mobileMenuButton: {
      background: 'transparent',
      border: 'none',
      color: COLORS.DARK_BROWN,
      fontSize: '22px',
      cursor: 'pointer',
      padding: '5px',
      display: isMobile ? 'block' : 'none'
    },
    navLinks: {
      display: isMobile ? (mobileMenuOpen ? 'flex' : 'none') : 'flex',
      alignItems: 'center',
      gap: '20px',
      flexDirection: isMobile ? 'column' : 'row',
      position: isMobile ? 'absolute' : 'static',
      top: isMobile ? '100%' : 'auto',
      left: 0,
      right: 0,
      background: isMobile ? COLORS.PRIMARY_YELLOW : 'transparent',
      padding: isMobile ? '15px' : 0,
      boxShadow: isMobile ? '0 4px 8px rgba(0,0,0,0.1)' : 'none',
      zIndex: 999
    },
    languageButton: {
      background: 'transparent',
      border: `1px solid ${COLORS.DARK_YELLOW}`,
      color: COLORS.DARK_BROWN,
      padding: '6px 12px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '15px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      transition: 'all 0.3s ease',
    },
    languageButtonActive: {
      background: COLORS.DARK_YELLOW,
      color: COLORS.WHITE
    },
    mainContent: {
      maxWidth: '900px',
      margin: '0 auto',
      padding: isMobile ? '15px' : '25px 20px',
      width: '100%',
      boxSizing: 'border-box',
      flex: '1'
    },
    // -------- Tabs (icon above label) --------
    tabsContainer: {
      background: 'transparent',
      marginBottom: '20px',
      width: '100%'
    },
    tabsHeader: {
      display: 'flex',
      background: COLORS.WHITE,
      borderBottom: `2px solid ${COLORS.DARK_YELLOW}`,
      width: '100%',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      gap: '0'
    },
    tabButton: {
      flex: '1 1 0',
      padding: isMobile ? '10px 4px' : '12px 10px',
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      fontSize: isMobile ? '12px' : '15px',
      fontWeight: '600',
      color: COLORS.DARK_GRAY,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      transition: 'all 0.3s ease',
      whiteSpace: 'normal',
      textAlign: 'center',
      minHeight: isMobile ? '70px' : '80px',
      wordBreak: 'break-word',
      minWidth: 0,
      fontFamily: "'Noto Serif Devanagari', serif",
    },
    tabButtonActive: {
      background: COLORS.PRIMARY_YELLOW,
      color: COLORS.DARK_BROWN,
      borderBottom: `3px solid ${COLORS.DARK_YELLOW}`
    },
    tabIcon: {
      fontSize: isMobile ? '18px' : '20px'
    },
    tabContent: {
      padding: 0
    },
    // -------- Note box --------
    noteBox: {
      background: COLORS.NOTE_BG,
      borderRadius: '10px',
      padding: isMobile ? '14px' : '18px 20px',
      marginBottom: '18px',
      color: COLORS.DARK_GRAY,
      fontSize: isMobile ? '15px' : '17px',
      lineHeight: '1.55',
      fontFamily: "'Noto Serif Devanagari', serif",
      fontWeight: '500'
    },
    // -------- Orange table header --------
    tableHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: COLORS.ORANGE_HEADER,
      color: COLORS.WHITE,
      padding: isMobile ? '14px 16px' : '16px 22px',
      borderRadius: '10px',
      fontSize: isMobile ? '16px' : '19px',
      fontWeight: '700',
      fontFamily: "'Noto Serif Devanagari', serif",
      marginBottom: '10px'
    },
    // -------- Stop rows --------
    routeColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      width: '100%'
    },
    stopCard: {
      background: COLORS.STOP_CARD_BG,
      border: 'none',
      borderRadius: '10px',
      padding: isMobile ? '12px 12px 12px 16px' : '14px 18px 14px 20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '12px',
      position: 'relative',
      width: '100%',
      boxSizing: 'border-box',
      textAlign: 'left'
    },
    // Orange vertical bar on left of each stop
    stopLeftBar: {
      position: 'absolute',
      left: 0,
      top: '10%',
      bottom: '10%',
      width: '4px',
      background: COLORS.ORANGE_BAR,
      borderRadius: '2px'
    },
    stopLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      flex: 1,
      minWidth: 0,
      alignItems: 'flex-start'
    },
    stopNameText: {
      fontWeight: '700',
      color: COLORS.DARK_GRAY,
      fontSize: isMobile ? '16px' : '19px',
      lineHeight: '1.3',
      wordBreak: 'break-word',
      fontFamily: "'Noto Serif Devanagari', serif",
      textAlign: 'left'
    },
    viewMapButton: {
      background: COLORS.GREEN,
      color: COLORS.WHITE,
      border: 'none',
      padding: isMobile ? '8px 12px' : '9px 16px',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: isMobile ? '13px' : '15px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      whiteSpace: 'nowrap',
      alignSelf: 'flex-start',
      fontFamily: "'Noto Serif Devanagari', serif"
    },
    // White pill with orange border on right
    timePill: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '8px 12px' : '10px 18px',
      border: `2px solid ${COLORS.ORANGE_BORDER}`,
      borderRadius: '10px',
      background: COLORS.WHITE,
      color: COLORS.ORANGE_TEXT,
      fontSize: isMobile ? '16px' : '19px',
      fontWeight: '700',
      flexShrink: 0,
      fontFamily: "'Noto Serif Devanagari', serif",
      minWidth: isMobile ? '78px' : '100px',
      whiteSpace: 'nowrap'
    },
    // -------- Contact / Apps sections --------
    contactSection: { marginBottom: '30px' },
    sectionTitle: {
      fontSize: '22px',
      fontWeight: '600',
      color: COLORS.DARK_GRAY,
      marginBottom: '15px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      borderBottom: `3px solid ${COLORS.DARK_YELLOW}`,
      paddingBottom: '12px', // Ensures space between text and the line
      width: 'fit-content',
      fontFamily: "'Noto Serif Devanagari', serif",
      marginTop:'10px'
    },
    contactList: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '15px'
    },
    contactItem: {
      background: '#f9f9f9',
      padding: '15px',
      borderRadius: '10px',
      borderLeft: `4px solid ${COLORS.DARK_YELLOW}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    contactNumber: {
      fontSize: '19px',
      fontWeight: '600',
      color: COLORS.DARK_GRAY,
      marginBottom: '5px'
    },
    websiteList: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '15px'
    },
    websiteItem: {
      background: '#f9f9f9',
      padding: '15px',
      borderRadius: '10px',
      borderLeft: `4px solid ${COLORS.GREEN}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '10px'
    },
    websiteName: { fontSize: '18px', fontWeight: '600', color: COLORS.DARK_GRAY },
    openButton: {
      background: COLORS.GREEN,
      color: COLORS.WHITE,
      border: 'none',
      padding: '12px 18px',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontSize: '16px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
    },
    callButton: {
      background: COLORS.GREEN,
      color: COLORS.WHITE,
      border: 'none',
      padding: '12px 18px',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      fontSize: '16px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
    },
    appList: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '15px'
    },
    appItem: {
      background: '#f9f9f9',
      padding: '20px',
      borderRadius: '10px',
      textAlign: 'center',
      border: `2px solid ${COLORS.GREEN}`,
      transition: 'transform 0.3s ease',
    },
    appName: { fontSize: '17px', fontWeight: '600', color: COLORS.DARK_GRAY, marginBottom: '15px' },
    appButton: {
      background: COLORS.GREEN,
      color: COLORS.WHITE,
      border: 'none',
      padding: '12px 20px',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      fontSize: '16px',
      fontWeight: '600',
      transition: 'all 0.3s ease',
    },
    actionButtons: {
      display: 'flex',
      gap: '15px',
      marginTop: '30px',
      justifyContent: 'center'
    },
    shareButton: {
      background: COLORS.DARK_YELLOW,
      color: COLORS.DARK_BROWN,
      border: 'none',
      padding: '15px 30px',
      borderRadius: '10px',
      fontSize: '18px',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 8px rgba(255, 179, 0, 0.3)',
    },
    copiedMessage: {
      position: 'fixed',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: COLORS.DARK_GRAY,
      color: COLORS.WHITE,
      padding: '12px 24px',
      borderRadius: '30px',
      boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
      zIndex: 1001,
      fontWeight: '600',
      fontSize: '16px',
      animation: 'slideUp 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      whiteSpace: 'nowrap'
    },
    copiedUrl: { color: COLORS.DARK_YELLOW, fontWeight: '700', textDecoration: 'underline', marginLeft: '4px' },
    footer: {
      background: COLORS.PRIMARY_YELLOW,
      color: COLORS.DARK_BROWN,
      padding: isMobile ? '15px 20px' : '20px 40px',
      textAlign: 'center',
      marginTop: '40px',
      width: '100%',
      boxSizing: 'border-box',
      fontFamily: "'Noto Serif Devanagari', serif",
      fontWeight: 'inherit',
      borderTop: `2px solid ${COLORS.DARK_YELLOW}`
    },
    footerText: { margin: '0 0 15px 0', opacity: '0.9', fontSize: isMobile ? '16px' : '17px' },
    footerLinks: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: isMobile ? '20px' : '30px',
      marginBottom: '5px'
    },
    footerLink: {
      color: COLORS.DARK_BROWN,
      textDecoration: 'none',
      opacity: '0.8',
      transition: 'opacity 0.3s ease, color 0.3s ease',
      fontSize: isMobile ? '16px' : '17px',
      padding: '5px 0',
      borderBottom: '1px solid transparent',
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      textAlign: 'center'
    },
    spinner: {
      width: isMobile ? '40px' : '50px',
      height: isMobile ? '40px' : '50px',
      border: '5px solid #f3f3f3',
      borderTop: '5px solid #FFB300',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '15px'
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px 20px',
      color: COLORS.MEDIUM_GRAY
    }
  };

  const getFooterText = () => {
    if (currentLanguage === 'hi') {
      return '© 2026 राजदूत एक्सप्लोरेशंस। सभी अधिकार सुरक्षित।';
    }
    return '© 2026 Rajdoot Explorations. All rights reserved.';
  };

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideUp {
        from { opacity: 0; transform: translate(-50%, 20px); }
        to { opacity: 1; transform: translate(-50%, 0); }
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ============================================================
  // FIX #3: Safer "hasData" check to prevent stuck blank/loading page.
  // ============================================================
  const hasData = busInfo || bus || rawBusData || forwardStops.length > 0;

  if (!hasData && !isLoadingStops) {
    return (
      <div style={{ textAlign: 'center', padding: '50px', fontSize: '18px' }}>
        {currentLanguage === 'hi' ? 'डेटा लोड हो रहा है...' : 'Loading...'}
      </div>
    );
  }

  if (isLoadingStops) {
    return (
      <div style={styles.container}>
        <header style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.logoContainer}>
              <button style={styles.backButton} onClick={() => navigate(-1)}>
                <FaArrowLeft /> {t('back') || 'Back'}
              </button>
              <img src={logo} alt="Rajdoot Logo" style={styles.logoImage} />
            </div>
          </div>
        </header>
        <main style={styles.mainContent}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={{ fontSize: '18px', color: COLORS.MEDIUM_GRAY }}>
              {currentLanguage === 'hi' ? 'स्टॉपेज सूची लोड हो रही है...' : 'Loading stoppage list...'}
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.logoContainer}>
          
            <img src={logo} alt="Rajdoot Logo" style={styles.logoImage} />
          </div>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {!isMobile && (
              <Link 
                to="/" 
                style={styles.homeButton}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.5)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
              >
                <FaHome /> {t('home', 'Home')}
              </Link>
            )}
            <button style={styles.mobileMenuButton} onClick={toggleMobileMenu}>
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
        {isMobile && mobileMenuOpen && (
          <nav style={styles.navLinks}>
            <a href="/" style={styles.footerLink} onClick={() => setMobileMenuOpen(false)}>
              <FaBus /> {currentLanguage === 'hi' ? 'होम' : 'Home'}
            </a>
          </nav>
        )}
      </header>

      <main style={styles.mainContent}>
        <div style={styles.tabsContainer}>
          {/* ===== TABS: icon above label ===== */}
          <div style={styles.tabsHeader}>
            <button
              style={{ ...styles.tabButton, ...(activeTab === 'timetable' ? styles.tabButtonActive : {}) }}
              onClick={() => setActiveTab('timetable')}
            >
              <FaClock style={styles.tabIcon} />
              <span>{currentLanguage === 'hi' ? 'समय सारिणी' : 'Time Table'}</span>
            </button>
            <button
              style={{ ...styles.tabButton, ...(activeTab === 'contact' ? styles.tabButtonActive : {}) }}
              onClick={() => setActiveTab('contact')}
            >
              <FaPhone style={styles.tabIcon} />
              <span>{currentLanguage === 'hi' ? 'फ़ोन नंबर' : 'Phone Number'}</span>
            </button>
            <button
              style={{ ...styles.tabButton, ...(activeTab === 'apps' ? styles.tabButtonActive : {}) }}
              onClick={() => setActiveTab('apps')}
            >
              <FaMobileAlt style={styles.tabIcon} />
              <span>{currentLanguage === 'hi' ? 'एप्प/वेबसाइट' : 'App/Website'}</span>
            </button>
          </div>

          <div style={styles.tabContent}>
            {/* ====================== TIMETABLE TAB ====================== */}
            {activeTab === 'timetable' && (
              <div>
                {/* Note box */}
                <div style={styles.noteBox}>
                  {currentLanguage === 'hi'
                    ? 'नोट - निम्नलिखित टेबल में बस स्टॉप/बस स्टैंड की स्थायी लोकेशन मौजूद है। बस के आगमन का वास्तविक समय दर्शाये गए समय से अलग हो सकता है।'
                    : 'Note - The table below contains the fixed location of the Bus Stop/Bus Stand. Actual arrival time of the bus may be different from the one shown here.'}
                </div>

                {forwardStops.length === 0 ? (
                  <div style={styles.emptyState}>
                    <FaMapMarkerAlt size={48} style={{ marginBottom: '15px', opacity: 0.5 }} />
                    <h3 style={{ fontSize: '20px' }}>
                      {currentLanguage === 'hi' ? 'कोई स्टॉपेज सूची उपलब्ध नहीं है' : 'No stoppage list available'}
                    </h3>
                    <p style={{ fontSize: '16px' }}>
                      {currentLanguage === 'hi'
                        ? 'इस बस के लिए कोई स्टॉप जानकारी नहीं मिली।'
                        : 'No stop information found for this bus.'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Orange table header */}
                    <div style={styles.tableHeader}>
                      <span>
                        {currentLanguage === 'hi' ? 'बस स्टॉप/बस स्टैंड' : 'Bus Stop/Bus Stand'}
                      </span>
                      <span>
                        {currentLanguage === 'hi' ? 'आगमन' : 'Arrival'}
                      </span>
                    </div>

                    <div style={styles.routeColumn}>
                      {forwardStops.map((stop) => (
                        <div key={stop.id} style={styles.stopCard}>
                          <div style={styles.stopLeftBar} />
                          <div style={styles.stopLeft}>
                            <div style={styles.stopNameText}>{stop.stopName}</div>
                            {stop.gps && (
                              <button
                                style={styles.viewMapButton}
                                onClick={() => openGPSLocation(stop.gps)}
                              >
                                <FaMapMarkerAlt />
                                {currentLanguage === 'hi' ? 'मैप में लोकेशन देखें' : 'View Location in Map'}
                                <FaExternalLinkAlt />
                              </button>
                            )}
                          </div>
                          <div style={styles.timePill}>
                            {stop.time || '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ====================== CONTACT TAB ====================== */}
            {activeTab === 'contact' && (
              <div>
                {phoneNumbers.booking.length > 0 && (
                  <div style={styles.contactSection}>
                    <h3 style={styles.sectionTitle}>
                      <FaPhone /> {currentLanguage === 'hi' ? 'फ़ोन नंबर' : 'Phone Numbers'}
                    </h3>
                    <div style={styles.contactList}>
                      {phoneNumbers.booking.map((contact) => (
                        <div key={contact.id} style={styles.contactItem}>
                          <div style={styles.contactNumber}>{contact.number}</div>
                          <button style={styles.callButton} onClick={() => makePhoneCall(contact.number)}>
                            <FaPhone /> {currentLanguage === 'hi' ? 'कॉल करें' : 'Call'} <FaExternalLinkAlt />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {phoneNumbers.operator.length > 0 && (
                  <div style={styles.contactSection}>
                    <h3 style={styles.sectionTitle}>
                      <MdDirectionsBus /> {currentLanguage === 'hi' ? 'बस संचालक के संपर्क-सूत्र' : 'Bus Operator Contact'}
                    </h3>
                    <div style={styles.contactList}>
                      {phoneNumbers.operator.map((contact) => (
                        <div key={contact.id} style={styles.contactItem}>
                          <div style={styles.contactNumber}>{contact.number}</div>
                          <button style={styles.callButton} onClick={() => makePhoneCall(contact.number)}>
                            <FaPhone /> {currentLanguage === 'hi' ? 'कॉल करें' : 'Call'} <FaExternalLinkAlt />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {phoneNumbers.booking.length === 0 && phoneNumbers.operator.length === 0 && (
                  <div style={styles.emptyState}>
                    <FaPhone size={40} style={{ marginBottom: '15px', opacity: 0.5 }} />
                    <p style={{ fontSize: '16px' }}>
                      {currentLanguage === 'hi' ? 'कोई संपर्क नंबर उपलब्ध नहीं है' : 'No contact numbers available'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ====================== APPS & WEBSITES TAB ====================== */}
            {activeTab === 'apps' && (
              <div>
                {websites.length > 0 && (
                  <div style={styles.contactSection}>
                    <h3 style={styles.sectionTitle} >
                      <FaGlobe style={{marginTop:10}}/> {currentLanguage === 'hi' ? 'वेबसाइट' : 'Website'}
                    </h3>
                    <div style={styles.websiteList}>
                      {websites.map((website) => (
                        <div key={website.id} style={styles.websiteItem}>
                          <div style={styles.websiteName}>{website.name}</div>
                          <button style={styles.openButton} onClick={() => window.open(website.url, '_blank')}>
                            <FaGlobe /> {currentLanguage === 'hi' ? 'वेबसाइट खोलें' : 'Open Website'} <FaExternalLinkAlt />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {apps.length > 0 && (
                  <div style={styles.contactSection}>
                    <h3 style={styles.sectionTitle}>
                      <FaMobileAlt /> {currentLanguage === 'hi' ? 'एप्प' : 'Mobile App'}
                    </h3>
                    <div style={styles.appList}>
                      {apps.map((app) => (
                        <div key={app.id} style={styles.appItem}>
                          <div style={styles.appName}>{app.name}</div>
                          <button style={styles.appButton} onClick={() => window.open(app.url, '_blank')}>
                            <FaGlobe /> {currentLanguage === 'hi' ? 'डाउनलोड करें' : 'Download App'} <FaExternalLinkAlt />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {websites.length === 0 && apps.length === 0 && (
                  <div style={styles.emptyState}>
                    <FaGlobe size={40} style={{ marginBottom: '15px', opacity: 0.5 }} />
                    <p style={{ fontSize: '16px' }}>
                      {currentLanguage === 'hi' ? 'कोई वेबसाइट या एप्प उपलब्ध नहीं है' : 'No websites or apps available'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={styles.actionButtons}>
          <button style={styles.shareButton} onClick={shareBusDetails}>
            <FaShareAlt /> {currentLanguage === 'hi' ? 'साझा करें' : 'Share'}
          </button>
        </div>
      </main>

      {showCopiedMessage && (
        <div style={styles.copiedMessage}>
          ✓ {currentLanguage === 'hi' ? 'लिंक कॉपी हो गया:' : 'Link copied:'}
          <span style={styles.copiedUrl}>{homepageUrl}</span>
        </div>
      )}

      <footer style={styles.footer}>
        <p style={styles.footerText}>{getFooterText()}</p>
        <div style={styles.footerLinks}>
          <a href="/privacy" style={styles.footerLink}>
            {currentLanguage === 'hi' ? 'गोपनीयता नीति' : 'Privacy Policy'}
          </a>
          <a href="/terms" style={styles.footerLink}>
            {currentLanguage === 'hi' ? 'सेवा की शर्तें' : 'Terms of Service'}
          </a>
          <a href="/contact" style={styles.footerLink}>
            {currentLanguage === 'hi' ? 'संपर्क करें' : 'Contact Us'}
          </a>
        </div>
      </footer>
    </div>
  );
};

export default BusDetails;